import React, { useState, useEffect } from 'react';
import { getFunnelItems, createFunnelItem, updateFunnelItem, deleteFunnelItem, getThreads } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { formatDate, getStatusColor } from '../lib/utils';
import { Plus, Users, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';

const FUNNEL_TYPES = ['Customers', 'Investors', 'Promoters', 'Donors', 'Partners', 'Employees', 'Regulators'];
const STAGES = ['Lead', 'Contacted', 'Qualified', 'Proposal', 'Negotiation', 'Closed', 'Lost'];

const FunnelsPage = () => {
  const [funnels, setFunnels] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filter, setFilter] = useState({ funnel_type: '' });
  const [formData, setFormData] = useState({
    party: '',
    funnel_type: 'Investors',
    stage: 'Lead',
    linked_thread_id: '',
    next_action_date: ''
  });

  const fetchData = async () => {
    try {
      const params = filter.funnel_type ? { funnel_type: filter.funnel_type } : {};
      const [funnelsRes, threadsRes] = await Promise.all([
        getFunnelItems(params),
        getThreads({})
      ]);
      setFunnels(funnelsRes.data);
      setThreads(threadsRes.data);
    } catch (error) {
      toast.error('Failed to load funnels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        linked_thread_id: formData.linked_thread_id || null,
        next_action_date: formData.next_action_date ? new Date(formData.next_action_date).toISOString() : null
      };

      if (editingItem) {
        await updateFunnelItem(editingItem.id, data);
        toast.success('Funnel item updated');
      } else {
        await createFunnelItem(data);
        toast.success('Funnel item created');
      }
      
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({ party: '', funnel_type: 'Investors', stage: 'Lead', linked_thread_id: '', next_action_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save funnel item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      party: item.party,
      funnel_type: item.funnel_type,
      stage: item.stage,
      linked_thread_id: item.linked_thread_id || '',
      next_action_date: item.next_action_date ? item.next_action_date.slice(0, 16) : ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this funnel item?')) return;
    try {
      await deleteFunnelItem(id);
      toast.success('Funnel item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Group by type
  const groupedFunnels = FUNNEL_TYPES.reduce((acc, type) => {
    acc[type] = funnels.filter(f => f.funnel_type === type);
    return acc;
  }, {});

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-zinc-500 font-inter">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="funnels-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope text-xl font-bold text-zinc-900">Funnels</h1>
          <p className="text-sm text-zinc-500 font-inter">Track relationships and pipeline</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingItem(null);
            setFormData({ party: '', funnel_type: 'Investors', stage: 'Lead', linked_thread_id: '', next_action_date: '' });
          }
        }}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="add-funnel-btn">
              <Plus className="w-4 h-4 mr-1" /> Add to Funnel
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-manrope">{editingItem ? 'Edit' : 'Add'} Funnel Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="font-inter text-sm">Party Name</Label>
                <Input
                  value={formData.party}
                  onChange={(e) => setFormData({ ...formData, party: e.target.value })}
                  className="rounded-sm"
                  placeholder="Company or Person name"
                  required
                  data-testid="funnel-party-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-inter text-sm">Type</Label>
                  <Select value={formData.funnel_type} onValueChange={(v) => setFormData({ ...formData, funnel_type: v })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FUNNEL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-inter text-sm">Stage</Label>
                  <Select value={formData.stage} onValueChange={(v) => setFormData({ ...formData, stage: v })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="font-inter text-sm">Linked Thread (optional)</Label>
                <Select value={formData.linked_thread_id || 'none'} onValueChange={(v) => setFormData({ ...formData, linked_thread_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select thread..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {threads.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-inter text-sm">Next Action Date</Label>
                <Input
                  type="datetime-local"
                  value={formData.next_action_date}
                  onChange={(e) => setFormData({ ...formData, next_action_date: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <Button type="submit" className="w-full rounded-sm" data-testid="submit-funnel-btn">
                {editingItem ? 'Update' : 'Add'} Funnel Item
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter */}
      <Select value={filter.funnel_type || 'all'} onValueChange={(v) => setFilter({ funnel_type: v === 'all' ? '' : v })}>
        <SelectTrigger className="w-40 rounded-sm">
          <SelectValue placeholder="All Types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          {FUNNEL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>

      {/* Funnel Grid */}
      {funnels.length === 0 ? (
        <Card className="border border-zinc-200 shadow-none rounded-sm">
          <CardContent className="p-8 text-center">
            <Users className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
            <p className="text-zinc-500 font-inter">No funnel items yet. Add manually or use Intake.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FUNNEL_TYPES.filter(type => !filter.funnel_type || filter.funnel_type === type).map(type => (
            groupedFunnels[type]?.length > 0 && (
              <Card key={type} className="border border-zinc-200 shadow-none rounded-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="font-manrope text-sm flex items-center justify-between">
                    {type}
                    <Badge variant="outline" className="font-mono">{groupedFunnels[type].length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {groupedFunnels[type].map(item => (
                    <div key={item.id} className="border border-zinc-100 p-2 rounded-sm text-sm">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium font-inter">{item.party}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`text-xs ${getStatusColor(item.stage)}`}>{item.stage}</Badge>
                            {item.next_action_date && (
                              <span className="text-xs text-zinc-500 font-mono">
                                {formatDate(item.next_action_date)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleEdit(item)}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )
          ))}
        </div>
      )}
    </div>
  );
};

export default FunnelsPage;
